import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {v4 as uuid} from "uuid";
import {validateJSON} from "../web/validation";

/** Validator for a dispatch-dispatch request association type. */
export const DispatchDispatchRequestAssocValidator = t.intersection([
    t.type({
        id: t.string,
        dispatchId: t.string,
        dispatchRequestId: t.string,
        regionId: t.string,
        status: t.string,
    }),
    t.partial({
        quantityDecimal: t.number,
        quantityUnitOfMeasure: t.string,
        driverId: t.string,
        driverName: t.string,
        trailerId: t.string,
        trailerName: t.string,
        dispatchResourceId: t.string,
        pickupDttm: t.number,
        deliveryDttm: t.number,
        dispatchDttm: t.number,
        createDttm: t.number,
        lastModifiedDttm: t.number,
    }),
]);

/** Validator for an array of dispatch-dispatch request association types. */
export const DispatchDispatchRequestAssocArrayValidator = t.array(
    DispatchDispatchRequestAssocValidator
);

export type DispatchDispatchRequestAssoc = t.TypeOf<
    typeof DispatchDispatchRequestAssocValidator
>;
export type DispatchDispatchRequestAssocArray = t.TypeOf<
    typeof DispatchDispatchRequestAssocArrayValidator
>;

/**
 * The status type of a dispatch-dispatch request association.
 */
export interface DispatchDispatchRequestAssocStatusType {
    code: string;
    displayValue: string;
}

const complete: DispatchDispatchRequestAssocStatusType = {
    code: 'COMPLETE',
    displayValue: 'Complete',
};

const started: DispatchDispatchRequestAssocStatusType = {
    code: 'STARTED',
    displayValue: 'Started',
};

const pickup: DispatchDispatchRequestAssocStatusType = {
    code: 'PICKUP',
    displayValue: 'Picked-Up',
};

const scheduled: DispatchDispatchRequestAssocStatusType = {
    code: 'SCHEDULED',
    displayValue: 'Scheduled',
};

const canceled: DispatchDispatchRequestAssocStatusType = {
    code: 'CANCELED',
    displayValue: 'Canceled',
};

/** Type of {@link DispatchDispatchRequestAssoc} for use when the type is unknown. */
export const unknownDispatchDispatchRequestAssocType: Readonly<DispatchDispatchRequestAssocStatusType> =
    Object.freeze<DispatchDispatchRequestAssocStatusType>({
        code: 'UNKNOWN',
        displayValue: 'Unknown',
    });

/**
 * Types for {@link DispatchDispatchRequestAssoc}.
 */
export const dispatchDispatchRequestAssocStatusTypes = {
    complete,
    pickup,
    scheduled,
    canceled,
    started,
};

/**
 * Return DispatchDispatchRequestAssocStatusType by code value.
 *
 * @param code - Code value
 * @returns {DispatchDispatchRequestAssocStatusType} The matching DispatchDispatchRequestAssocStatusType
 */
export const toDispatchDispatchRequestAssocStatusType = (
    code: string
): DispatchDispatchRequestAssocStatusType => {
    for (const type of Object.values(dispatchDispatchRequestAssocStatusTypes)) {
        if (type.code === code) {
            return type;
        }
    }
    return {code, displayValue: code};
};

/**
 * A dispatch-dispatch request association.
 */
@Exclude()
export class DispatchDispatchRequestAssocEntity {
    @Expose() id!: string;
    @Expose() dispatchId!: string;
    @Expose() dispatchRequestId!: string;
    @Expose() regionId!: string;
    @Expose({name: 'status'}) private _status!: string;
    @Expose({name: 'quantityDecimal'}) quantity?: number;
    @Expose({name: 'quantityUnitOfMeasure'}) quantityUom?: string;
    @Expose() driverId?: string;
    @Expose() driverName?: string;
    @Expose() trailerId?: string;
    @Expose() trailerName?: string;
    @Expose() dispatchResourceId?: string;
    @Expose() pickupDttm?: number;
    @Expose() deliveryDttm?: number;
    @Expose() dispatchDttm?: number;
    @Expose() createDttm?: number;
    @Expose() lastModifiedDttm?: number;

    /**
     * Constructor for DispatchDispatchRequestAssocEntity. Recommend using the {@link create} method instead.
     * @hideconstructor
     */
    constructor() {
        // Empty constructor for 'class-transformer'
    }

    /**
     * Create a DispatchDispatchRequestAssocEntity.
     *
     * @param dispatchId The primary key of the dispatch record.
     * @param dispatchRequestId The primary key of the dispatch request record.
     * @param regionId The primary key of the region.
     * @param id (optional) The primary key of the dispatchDispatchRequestAssoc.
     */
    static create(
        dispatchId: string,
        dispatchRequestId: string,
        regionId: string,
        id: string | undefined = uuid()
    ): DispatchDispatchRequestAssocEntity {
        const entity = new DispatchDispatchRequestAssocEntity();
        entity.id = id;
        entity.dispatchId = dispatchId;
        entity.dispatchRequestId = dispatchRequestId;
        entity.regionId = regionId;
        entity._status = scheduled.code;
        return entity;
    }

    /**
     * Create a DispatchDispatchRequestAssocEntity from a DispatchDispatchRequestAssoc type value.
     *
     * @param dispatchDispatchRequestAssoc The dispatchDispatchRequestAssoc type value.
     */
    static from(
        dispatchDispatchRequestAssoc: DispatchDispatchRequestAssoc
    ): DispatchDispatchRequestAssocEntity {
        return plainToInstance(
            DispatchDispatchRequestAssocEntity,
            dispatchDispatchRequestAssoc,
            {
                excludeExtraneousValues: true,
            }
        );
    }

    /**
     * Deserializes the DispatchDispatchRequestAssocEntity to a DispatchDispatchRequestAssoc type value.
     * If validation fails, the promise will be rejected.
     */
    toTypeValue(): Promise<DispatchDispatchRequestAssoc> {
        const object = instanceToPlain(this, {
            excludeExtraneousValues: true,
        });
        return validateJSON(DispatchDispatchRequestAssocValidator, object);
    }

    get status(): DispatchDispatchRequestAssocStatusType {
        /* istanbul ignore next */
        return toDispatchDispatchRequestAssocStatusType(this._status);
    }

    set status(status: DispatchDispatchRequestAssocStatusType) {
        /* istanbul ignore next */
        this._status = status.code;
    }

    get quantityDisplay(): string {
        if (this.quantity && this.quantityUom) {
            return this.quantity + ' ' + this.quantityUom;
        } else if (this.quantity) {
            return this.quantity.toString();
        }
        return '-';
    }
}
